import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import dealsImg from '@/assets/banners/category-deals.jpg';
import smartHomeImg from '@/assets/banners/category-smart-home.jpg';
import phoneImg from '@/assets/banners/category-phone.jpg';
import audioImg from '@/assets/banners/category-audio.jpg';
import lightingImg from '@/assets/banners/category-lighting.jpg';

type Banner = {
  href: string;
  image: string;
  alt: string;
  badge: string;
  headline: string;
  subline: string;
  accent: 'primary' | 'destructive' | 'accent' | 'foreground';
  cta?: string;
};

const banners: Banner[] = [
  {
    href: '/deals',
    image: dealsImg,
    alt: 'Discounted tech gadgets bundle: earbuds, power bank, USB-C charger, cable and smart plug',
    badge: 'Up to -50%',
    headline: 'Hot deals',
    subline: 'Limited-time offers on top gadgets',
    accent: 'destructive',
    cta: 'View deals',
  },
  {
    href: '/catalog/smart-home',
    image: smartHomeImg,
    alt: 'Smart home devices',
    badge: 'New arrivals',
    headline: 'Smart home',
    subline: 'Cameras, plugs, vacuums & more',
    accent: 'primary',
  },
  {
    href: '/catalog/phone-accessories',
    image: phoneImg,
    alt: 'Phone accessories',
    badge: 'Essentials',
    headline: 'Phone accessories',
    subline: 'Chargers, cases, holders & cables',
    accent: 'foreground',
  },
  {
    href: '/catalog/audio-wearables',
    image: audioImg,
    alt: 'Audio and wearables',
    badge: 'Bestsellers',
    headline: 'Audio & wearables',
    subline: 'Headphones, earbuds & smart watches',
    accent: 'accent',
  },
  {
    href: '/catalog/lighting',
    image: lightingImg,
    alt: 'Lighting and decor',
    badge: 'Mood',
    headline: 'Lighting & decor',
    subline: 'LED strips, projectors & night lights',
    accent: 'primary',
  },
];

const accentClasses: Record<Banner['accent'], string> = {
  primary: 'bg-primary text-primary-foreground',
  destructive: 'bg-destructive text-destructive-foreground',
  accent: 'bg-accent text-accent-foreground',
  foreground: 'bg-foreground text-background',
};

const PromoCard: React.FC<{ banner: Banner; eager: boolean }> = ({ banner, eager }) => {
  return (
    <Link
      to={banner.href}
      className="group relative flex flex-col w-full overflow-hidden rounded-2xl bg-card border border-border shadow-sm hover:shadow-lg transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      aria-label={`${banner.headline} — ${banner.subline}`}
    >
      <article className="flex flex-col h-full">
        <div className="relative aspect-square bg-muted overflow-hidden">
          <img
            src={banner.image}
            alt={banner.alt}
            width={400}
            height={400}
            loading={eager ? 'eager' : 'lazy'}
            fetchPriority={eager ? 'high' : undefined}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <span
            className={cn(
              'absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-bold shadow-sm',
              accentClasses[banner.accent],
            )}
          >
            {banner.badge}
          </span>
        </div>

        <div className="flex flex-col flex-1 p-4 gap-2">
          <h3 className="font-semibold text-base leading-snug line-clamp-2 group-hover:text-primary transition-colors">
            {banner.headline}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2">{banner.subline}</p>

          <div className="mt-auto pt-3 flex items-center justify-end">
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:gap-2 transition-all">
              {banner.cta || 'Shop now'}
              <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
};

export const PromoBannerRow: React.FC = () => {
  return (
    <section className="container-wide py-6 md:py-10" aria-label="Featured promotions">
      {/* Mobile: snap scroll. Desktop: grid */}
      <div
        className="
          -mx-4 px-4 sm:-mx-6 sm:px-6 md:mx-0 md:px-0
          scroll-pl-4 sm:scroll-pl-6 md:scroll-pl-0
          scroll-pr-4 sm:scroll-pr-6 md:scroll-pr-0
          flex md:grid gap-4 items-stretch
          md:grid-cols-3 lg:grid-cols-5
          overflow-x-auto md:overflow-visible
          snap-x snap-proximity md:snap-none
          py-2 md:py-0
          scrollbar-hide
        "
      >
        {banners.map((b, i) => (
          <div
            key={b.href}
            className="snap-start shrink-0 w-[78%] sm:w-[45%] md:w-auto flex"
          >
            <PromoCard banner={b} eager={i < 3} />
          </div>
        ))}
      </div>
    </section>
  );
};